import React from "react";
import { Box, Typography } from "@mui/material";
import { usePermissions } from "@/hooks/usePermissions";
import { SectionHeader } from "./SectionHeader";
import Cookies from "js-cookie";

interface ValueItem {
  oppNo?: string | number | null;
  name?: string | null;
  value?: number | null;
  daysAgo?: number | string | null;
}

interface ExtremeSection {
  title: string;
  max?: ValueItem | null;
  min?: ValueItem | null;
}

interface ExtremeValueGridProps {
  title: string;
  data: ExtremeSection[];
  onRefresh?: () => void;
  singleColumn?: boolean;
}

const ExtremeValueGrid: React.FC<ExtremeValueGridProps> = ({
  title,
  data,
  onRefresh,
  singleColumn,
}) => {
  const { currentUser, permissionLoading } = usePermissions();

  const currencySymbol =
    Cookies.get("mobile")
      ? JSON.parse(Cookies.get("mobile")!).currencySymbol
      : currentUser?.CompanyDetail?.CurrencySymbol ?? "";

  if (permissionLoading) return null;

  return (
    <Box sx={{ mt: 2, borderRadius: 2, overflow: "hidden", boxShadow: 1 }}>
      {/* ✅ Section Header */}
      <SectionHeader title={title} {...(onRefresh ? { onRefresh } : {})} />

      {/* ✅ Main Content */}
      <Box
        sx={{
          bgcolor: "#fff",
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {data.length === 0 ? (
          <Typography sx={{ color: "#666", textAlign: "center", py: 3 }}>
            No data available
          </Typography>
        ) : (
          data.map((section, idx) => (
            <Box
              key={idx}
              sx={{
                display: "flex",
                flexDirection: singleColumn ? "column" : "row",
                flexWrap: singleColumn ? "nowrap" : "wrap",
                justifyContent: "space-between",
                borderBottom:
                  idx !== data.length - 1 ? "1px solid #ddd" : "none",
                pb: 1.5,
                gap: 2,
              }}
            >
              {/* 🔹 Maximum Side */}
              <Box sx={{ flex: singleColumn ? "1 1 100%" : "1 1 48%", width: singleColumn ? "100%" : undefined }}>
                <Box sx={{display : "flex",justifyContent : "space-between"}}>

                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#2e4734",
                    fontSize: 13,
                    mb: 1,
                    textDecoration: "underline",
                  }}
                >
                  Maximum {section.title}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#666" }}>
                  {section.max?.daysAgo ?? "-"}
                </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: 12 }}>
                      Opp No.
                    </Typography>
                    <Typography>{section.max?.oppNo ?? "-"}</Typography>
                  </Box>

                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: 12 }}>
                      Opp Name
                    </Typography>
                    <Typography>{section.max?.name ?? "-"}</Typography>
                  </Box>

                  <Box textAlign="right">
                    <Typography sx={{ fontWeight: 600, minWidth: 90, fontSize: 12 }}>
                      Value
                      {`(${currencySymbol})`}
                    </Typography>
                    <Typography>{section.max?.value ?? "-"}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* 🔹 Minimum Side */}
              <Box sx={{ flex: singleColumn ? "1 1 100%" : "1 1 48%", width: singleColumn ? "100%" : undefined }}>
                <Box sx={{display : "flex",justifyContent : "space-between"}}>

                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#2e4734",
                    fontSize: 13,
                    mb: 1,
                    textDecoration: "underline",
                  }}
                >
                  Minimum {section.title}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#666" }}>
                  {section.min?.daysAgo ?? "-"}
                </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: 12 }}>
                      Opp No.
                    </Typography>
                    <Typography>{section.min?.oppNo ?? "-"}</Typography>
                  </Box>

                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: 12 }}>
                      Opp Name
                    </Typography>
                    <Typography>{section.min?.name ?? "-"}</Typography>
                  </Box>

                  <Box textAlign="right">
                    <Typography sx={{ fontWeight: 600, minWidth: 90, fontSize: 12 }}>
                      Value
                      {`(${currencySymbol})`}
                    </Typography>
                    <Typography>{section.min?.value ?? "-"}</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default ExtremeValueGrid;
